import React, {createContext, useContext, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {Form, FormikProvider, useFormik} from 'formik';
import {format} from 'date-fns';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {usePaymentContext} from '../PaymentContext';
import {Currency, InvoiceOrderDocument} from '../../../../common/types';
import {invoiceOrderTotalWithVat} from '../../../../common/utils';
import {currencies} from '../../../../common/enums';

export type PaymentRecurringContext = {
  currencyOptions: {label: string, value: Currency}[]
  loading: boolean
};

export type PaymentRecurringContextProviderProps = {children: React.ReactNode}

export const defaultContext: PaymentRecurringContext = {
  currencyOptions: [],
  loading: false
};

export type FormValues = {
  amount: string,
  description: string,
  recurringPeriod: number,
  paymentsCount: string,
  startDate: Date | null,
  currency: Currency,
  customerName: string,
  email: string,
  phone: string,
}

export const defaultFormValues: FormValues = {
  amount: '',
  description: '',
  recurringPeriod: 1,
  paymentsCount: '',
  startDate: null,
  currency: 'ils',
  customerName: '',
  email: '',
  phone: '',
};

export const PaymentRecurringContext = createContext<PaymentRecurringContext>(defaultContext);
PaymentRecurringContext.displayName = 'PaymentRecurringContext';

export const usePaymentRecurringContext = () => useContext(PaymentRecurringContext);

export const PaymentRecurringContextProvider = ({children}: PaymentRecurringContextProviderProps) => {
  const M = useMessage();
  const intl = useIntl();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const [loading, setLoading] = useState<boolean>(false);

  const {updateLink, invoiceOrder} = usePaymentContext();

  const formik = useFormik<FormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof FormValues]: string }> = {};
      if (!values.amount) errors.amount = M.requiredField;
      if (values.amount && parseInt(values.amount) < 5) errors.amount = M.amountLessThan5Error;
      if (!values.description) errors.description = M.requiredField;
      if (!values.recurringPeriod) errors.recurringPeriod = M.requiredField;
      if (!values.paymentsCount) errors.paymentsCount = M.requiredField;
      if (!values.startDate) errors.startDate = M.requiredField;

      return errors;
    },
    onSubmit: async (values, {resetForm}) => {
      setLoading(true);
      try {
        const result = await apiService.getPaymentRecurringPage(
          parseFloat(values.amount),
          values.description,
          values.recurringPeriod,
          parseInt(values.paymentsCount),
          values.startDate ? format(values.startDate, 'dd/MM/yyyy') : '',
          values.customerName,
          values.email,
          values.phone,
        );
        if (result.errorCode < 0) {
          globalMessage.open(E.serverError);
        } else {
          updateLink(result.body.url);
          resetForm();
        }
      } catch (e) {
        globalMessage.open(E.serverError);
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true,
  });

  const update = async (invoiceOrderDTO: InvoiceOrderDocument) => {
    const invoiceOrder = {
      ...invoiceOrderDTO,
      service: invoiceOrderDTO.lines.map(line => line.description).join(', '),
      lines: invoiceOrderDTO.lines.map(line => ({
        ...line,
        amount: line.amount.toString(),
      }))
    };

    await formik.setValues({
      ...defaultFormValues,
      amount: invoiceOrderTotalWithVat(invoiceOrder, new Date(invoiceOrder.createdAt)).toFixed(2),
      description: invoiceOrder.service,
      customerName: invoiceOrder.customerName,
      email: invoiceOrder.emailRecipients,
      phone: invoiceOrder.phone,
    });
  };

  const updateForm = () => {
    if (invoiceOrder) {
      update(invoiceOrder).catch(console.error);
    }
  };

  useEffect(() => {
    updateForm();
  }, [invoiceOrder]);

  const currencyOptions = [
    {
      label: intl.formatMessage({
        id: 'payment-currency-NIS',
        defaultMessage: 'NIS'
      }),
      value: currencies.NIS
    },
    {
      label: intl.formatMessage({
        id: 'payment-currency-USD',
        defaultMessage: 'USD'
      }),
      value: currencies.USD
    },
    {
      label: intl.formatMessage({
        id: 'payment-currency-EUR',
        defaultMessage: 'EUR'
      }),
      value: currencies.EUR
    },
  ];

  const context: PaymentRecurringContext = {
    ...defaultContext,
    currencyOptions,
    loading
  };

  return (
    <PaymentRecurringContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </PaymentRecurringContext.Provider>
  );
};
